import { useEffect, useState } from "react";
import {
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Line,
  ReferenceLine,
} from "recharts";
import { getMarketDataSymbols, getMarketData, getIntradayBars, listAdjustmentFactors, getFinnhubSubscriptions, subscribeFinnhub, unsubscribeFinnhub, type MarketDataPoint, type IntradayBar, type AdjustmentFactor } from "../api/client";
import { format, subWeeks } from "date-fns";
import AdjustmentFactorsPanel from "../components/MarketData/AdjustmentFactorsPanel";
import DateRangeSelector from "../components/DateRangeSelector";
import { getDateRangeStart, type DateRangeKey } from "../utils/dateRange";

export default function MarketDataPage() {
  const [symbols, setSymbols] = useState<string[]>([]);
  const [selectedSymbol, setSelectedSymbol] = useState<string | null>(null);
  const [series, setSeries] = useState<MarketDataPoint[]>([]);
  const [intradayBars, setIntradayBars] = useState<IntradayBar[]>([]);
  const [loadingSymbols, setLoadingSymbols] = useState(false);
  const [loadingSeries, setLoadingSeries] = useState(false);
  const [loadingIntraday, setLoadingIntraday] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 640);
  const [showAdjusted, setShowAdjusted] = useState(true);
  const [dateRange, setDateRange] = useState<DateRangeKey>("1Y");
  const [adjustmentFactors, setAdjustmentFactors] = useState<AdjustmentFactor[]>([]);
  const [subscriptions, setSubscriptions] = useState<Set<string>>(new Set());
  const [togglingSubscription, setTogglingSubscription] = useState<string | null>(null);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  useEffect(() => {
    setLoadingSymbols(true);
    getMarketDataSymbols()
      .then((res) => {
        const sorted = [
          ...res.data.filter((s: string) => !s.includes("/")),
          ...res.data.filter((s: string) => s.includes("/")),
        ];
        setSymbols(sorted);
        if (sorted.length > 0) setSelectedSymbol(sorted[0]);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoadingSymbols(false));

    // Load current Finnhub subscriptions
    getFinnhubSubscriptions()
      .then((res) => setSubscriptions(new Set(res.data.symbols)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedSymbol || selectedSymbol.includes("/")) {
      setAdjustmentFactors([]);
      return;
    }
    listAdjustmentFactors(selectedSymbol)
      .then((res) => setAdjustmentFactors(res.data))
      .catch(() => setAdjustmentFactors([]));
  }, [selectedSymbol]);

  useEffect(() => {
    if (!selectedSymbol) return;
    setLoadingSeries(true);
    setError(null);
    const startDate = getDateRangeStart(dateRange);
    getMarketData({ symbol: selectedSymbol, adjusted: showAdjusted, start_date: startDate })
      .then((res) => setSeries([...res.data].reverse()))
      .catch((err) => setError(err.message))
      .finally(() => setLoadingSeries(false));

    setLoadingIntraday(true);
    setIntradayBars([]);
    const oneWeekAgo = subWeeks(new Date(), 1).toISOString();
    getIntradayBars({ symbol: selectedSymbol, start: oneWeekAgo, limit: 2500 })
      .then((res) => setIntradayBars([...res.data].reverse()))
      .catch((err) => { console.error("Failed to load intraday bars:", err); setIntradayBars([]); })
      .finally(() => setLoadingIntraday(false));
  }, [selectedSymbol, showAdjusted, dateRange]);

  const stockSymbols = symbols.filter((s) => !s.includes("/"));
  const currencySymbols = symbols.filter((s) => s.includes("/"));

  const handleToggleSubscription = async (sym: string) => {
    setTogglingSubscription(sym);
    try {
      if (subscriptions.has(sym)) {
        await unsubscribeFinnhub(sym);
        setSubscriptions((prev) => { const s = new Set(prev); s.delete(sym); return s; });
      } else {
        await subscribeFinnhub(sym);
        setSubscriptions((prev) => new Set([...prev, sym]));
      }
    } catch (err) {
      console.error(`Failed to toggle subscription for ${sym}:`, err);
    } finally {
      setTogglingSubscription(null);
    }
  };

  const formatted = series.map((d) => ({
    ...d,
    dateLabel: format(new Date(d.date), "MMM d, yy"),
    closeValue: d.close != null ? Number(d.close) : null,
  }));

  // For each adjustment factor, find the closest data point in the visible range
  const adjustmentAnnotations = showAdjusted
    ? adjustmentFactors
        .map((af) => {
          if (formatted.length === 0) return null;
          const afTime = new Date(af.effective_date).getTime();
          let closest = formatted[0];
          let minDiff = Infinity;
          for (const dp of formatted) {
            const diff = Math.abs(new Date(dp.date).getTime() - afTime);
            if (diff < minDiff) { minDiff = diff; closest = dp; }
          }
          return { ...af, dateLabel: closest.dateLabel };
        })
        .filter((a): a is AdjustmentFactor & { dateLabel: string } => a !== null)
    : [];

  // Enrich formatted data with adjustment info for tooltip
  const formattedWithInfo = formatted.map((d) => ({
    ...d,
    adjustmentInfo: adjustmentAnnotations.find((a) => a.dateLabel === d.dateLabel) ?? null,
  }));

  const formattedIntraday = intradayBars.map((b) => {
    const openValue = b.open != null ? Number(b.open) : null;
    const highValue = b.high != null ? Number(b.high) : null;
    const lowValue = b.low != null ? Number(b.low) : null;
    const closeValue = b.close != null ? Number(b.close) : null;
    return {
      ...b,
      timeLabel: format(new Date(b.timestamp), "HH:mm"),
      openValue,
      highValue,
      lowValue,
      closeValue,
      // Range [low, high] used by recharts Bar for candlestick span
      highLow: highValue != null && lowValue != null ? [lowValue, highValue] as [number, number] : null,
    };
  });

  // Vertical separator lines between trading days for the intraday chart
  const intradayDayBoundaries = formattedIntraday.reduce<{ timestamp: string; dateStr: string }[]>(
    (acc, bar, i) => {
      if (i === 0) return acc;
      const barDate = format(new Date(String(bar.timestamp)), "yyyy-MM-dd");
      const prevDate = format(new Date(String(formattedIntraday[i - 1].timestamp)), "yyyy-MM-dd");
      if (barDate !== prevDate) {
        const barDt = new Date(String(bar.timestamp));
        acc.push({
          timestamp: String(bar.timestamp),
          dateStr: format(barDt, "MMM d"),
        });
      }
      return acc;
    },
    [],
  );

  const formatValue = (v: number) =>
    new Intl.NumberFormat("en-US", { maximumFractionDigits: 4 }).format(v);

  return (
    <div>
      <h2 style={{ margin: "0 0 16px", color: "#1e293b" }}>Market Data</h2>

      {error && (
        <div style={{ color: "#dc2626", marginBottom: 16 }}>Error: {error}</div>
      )}

      {/* Mobile: compact dropdown */}
      {isMobile && (
        <div style={{ marginBottom: 16 }}>
          <select
            value={selectedSymbol ?? ""}
            onChange={(e) => setSelectedSymbol(e.target.value || null)}
            disabled={loadingSymbols}
            style={{
              width: "100%",
              padding: "8px 12px",
              border: "1px solid #e2e8f0",
              borderRadius: 8,
              fontSize: 14,
              fontFamily: "monospace",
              color: "#1e293b",
              background: "#fff",
              cursor: "pointer",
            }}
          >
            {symbols.length === 0 && <option value="">No symbols available</option>}
            {stockSymbols.length > 0 && (
              <optgroup label="Stocks">
                {stockSymbols.map((sym) => (
                  <option key={sym} value={sym}>{sym}</option>
                ))}
              </optgroup>
            )}
            {currencySymbols.length > 0 && (
              <optgroup label="Currencies">
                {currencySymbols.map((sym) => (
                  <option key={sym} value={sym}>{sym}</option>
                ))}
              </optgroup>
            )}
          </select>
        </div>
      )}

      <div style={{ display: "flex", gap: 24, alignItems: "flex-start" }}>
        {/* Desktop: symbol sidebar */}
        {!isMobile && (
          <div
            style={{
              width: 200,
              background: "#fff",
              border: "1px solid #e2e8f0",
              borderRadius: 8,
              overflow: "hidden",
              flexShrink: 0,
            }}
          >
            <div
              style={{
                padding: "10px 16px",
                background: "#f1f5f9",
                fontWeight: 600,
                fontSize: 13,
                color: "#475569",
                borderBottom: "1px solid #e2e8f0",
              }}
            >
              Symbols {loadingSymbols ? "…" : `(${symbols.length})`}
            </div>
            {symbols.length === 0 && !loadingSymbols && (
              <div style={{ padding: 16, color: "#94a3b8", fontSize: 13 }}>
                No market data available yet. Import a Flex XML or fetch via Alpha Vantage.
              </div>
            )}
            <div style={{ maxHeight: 480, overflowY: "auto" }}>
              {stockSymbols.map((sym) => (
                <div
                  key={sym}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    borderBottom: "1px solid #f1f5f9",
                    background: selectedSymbol === sym ? "#eff6ff" : "transparent",
                  }}
                >
                  <button
                    onClick={() => setSelectedSymbol(sym)}
                    style={{
                      flex: 1,
                      textAlign: "left",
                      padding: "8px 12px",
                      background: "transparent",
                      color: selectedSymbol === sym ? "#1d4ed8" : "#334155",
                      border: "none",
                      cursor: "pointer",
                      fontSize: 13,
                      fontFamily: "monospace",
                      fontWeight: selectedSymbol === sym ? 700 : 400,
                    }}
                  >
                    {sym}
                  </button>
                  <button
                    onClick={() => handleToggleSubscription(sym)}
                    disabled={togglingSubscription === sym}
                    title={subscriptions.has(sym) ? `Unsubscribe ${sym}` : `Subscribe ${sym} to intraday feed`}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: togglingSubscription === sym ? "not-allowed" : "pointer",
                      fontSize: 14,
                      padding: "4px 6px",
                      color: subscriptions.has(sym) ? "#16a34a" : "#94a3b8",
                      lineHeight: 1,
                      flexShrink: 0,
                    }}
                  >
                    {subscriptions.has(sym) ? "📡" : "○"}
                  </button>
                </div>
              ))}
              {stockSymbols.length > 0 && currencySymbols.length > 0 && (
                <div
                  style={{
                    padding: "6px 16px",
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#94a3b8",
                    background: "#f8fafc",
                    borderTop: "1px solid #e2e8f0",
                    borderBottom: "1px solid #e2e8f0",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Currencies
                </div>
              )}
              {currencySymbols.map((sym) => (
                <div
                  key={sym}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    borderBottom: "1px solid #f1f5f9",
                    background: selectedSymbol === sym ? "#eff6ff" : "transparent",
                  }}
                >
                  <button
                    onClick={() => setSelectedSymbol(sym)}
                    style={{
                      flex: 1,
                      textAlign: "left",
                      padding: "8px 12px",
                      background: "transparent",
                      color: selectedSymbol === sym ? "#1d4ed8" : "#334155",
                      border: "none",
                      cursor: "pointer",
                      fontSize: 13,
                      fontFamily: "monospace",
                      fontWeight: selectedSymbol === sym ? 700 : 400,
                    }}
                  >
                    {sym}
                  </button>
                  <button
                    onClick={() => handleToggleSubscription(sym)}
                    disabled={togglingSubscription === sym}
                    title={subscriptions.has(sym) ? `Unsubscribe ${sym}` : `Subscribe ${sym} to intraday feed`}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: togglingSubscription === sym ? "not-allowed" : "pointer",
                      fontSize: 14,
                      padding: "4px 6px",
                      color: subscriptions.has(sym) ? "#16a34a" : "#94a3b8",
                      lineHeight: 1,
                      flexShrink: 0,
                    }}
                  >
                    {subscriptions.has(sym) ? "📡" : "○"}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Chart */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 20, marginBottom: 20 }}>
          {selectedSymbol ? (
            <>
              <h3 style={{ margin: "0 0 12px", color: "#1e293b", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                {selectedSymbol}
                {loadingSeries && (
                  <span style={{ fontWeight: 400, fontSize: 13, color: "#94a3b8", marginLeft: 8 }}>
                    Loading…
                  </span>
                )}
                <button
                  onClick={() => setShowAdjusted((v) => !v)}
                  style={{
                    marginLeft: "auto",
                    padding: "4px 12px",
                    borderRadius: 6,
                    border: "1px solid #cbd5e1",
                    background: showAdjusted ? "#3b82f6" : "#f8fafc",
                    color: showAdjusted ? "#fff" : "#475569",
                    cursor: "pointer",
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  {showAdjusted ? "Adjusted ✓" : "Show Adjusted"}
                </button>
              </h3>
              <div style={{ marginBottom: 16 }}>
                <DateRangeSelector value={dateRange} onChange={setDateRange} />
              </div>
              {!loadingSeries && formattedWithInfo.length === 0 && (
                <p style={{ color: "#94a3b8" }}>No data for this symbol.</p>
              )}
              {formattedWithInfo.length > 0 && (
                <ResponsiveContainer width="100%" height={360}>
                  <ComposedChart data={formattedWithInfo} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis
                      dataKey="dateLabel"
                      tick={{ fontSize: 11 }}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      tickFormatter={formatValue}
                      tick={{ fontSize: 11 }}
                      width={80}
                      domain={["auto", "auto"]}
                    />
                    <Tooltip
                      content={({ active, payload, label }) => {
                        if (!active || !payload || payload.length === 0) return null;
                        const d = payload[0]?.payload as (typeof formattedWithInfo)[0];
                        return (
                          <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 6, padding: "8px 12px", fontSize: 12 }}>
                            <div style={{ fontWeight: 600, marginBottom: 4 }}>Date: {label}</div>
                            <div>Close: {d.closeValue != null ? formatValue(d.closeValue) : "—"}</div>
                            {d.adjustmentInfo && (
                              <div style={{ marginTop: 6, borderTop: "1px solid #e2e8f0", paddingTop: 6 }}>
                                <div style={{ fontWeight: 600, color: "#f59e0b" }}>⚠ Price Adjustment</div>
                                <div>Factor: ×{Number(d.adjustmentInfo.factor).toPrecision(6).replace(/\.?0+$/, "")}</div>
                                {d.adjustmentInfo.note && <div>Note: {d.adjustmentInfo.note}</div>}
                                <div style={{ color: "#94a3b8" }}>Effective: {d.adjustmentInfo.effective_date}</div>
                              </div>
                            )}
                          </div>
                        );
                      }}
                    />
                    {adjustmentAnnotations.map((af) => (
                      <ReferenceLine
                        key={af.id}
                        x={af.dateLabel}
                        stroke="#f59e0b"
                        strokeDasharray="4 2"
                        strokeWidth={1.5}
                        label={{ value: "✂", position: "insideTopRight", fontSize: 11, fill: "#f59e0b" }}
                      />
                    ))}
                    <Line
                      type="monotone"
                      dataKey="closeValue"
                      stroke="#3b82f6"
                      strokeWidth={2}
                      dot={false}
                      name="Close"
                      connectNulls
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              )}
            </>
          ) : (
            <p style={{ color: "#94a3b8" }}>Select a symbol to view its time series.</p>
          )}
          </div>

          {/* Intraday line chart */}
          {selectedSymbol && !loadingIntraday && formattedIntraday.length > 0 && (
            <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 20 }}>
              <h3 style={{ margin: "0 0 16px", color: "#1e293b" }}>
                {selectedSymbol} — Intraday Close (1-min bars)
              </h3>
              <ResponsiveContainer width="100%" height={320}>
                <ComposedChart data={formattedIntraday} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    dataKey="timestamp"
                    tick={{ fontSize: 11 }}
                    interval={Math.max(0, Math.floor(formattedIntraday.length / 10) - 1)}
                    tickFormatter={(v: string) => format(new Date(v), "HH:mm")}
                  />
                  <YAxis
                    tickFormatter={formatValue}
                    tick={{ fontSize: 11 }}
                    width={80}
                    domain={["auto", "auto"]}
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload || payload.length === 0) return null;
                      const d = payload[0]?.payload as typeof formattedIntraday[0];
                      return (
                        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 6, padding: "8px 12px", fontSize: 12 }}>
                          <div style={{ fontWeight: 600, marginBottom: 4 }}>{format(new Date(d.timestamp), "MMM d HH:mm")}</div>
                          <div>Close: {d.closeValue != null ? formatValue(d.closeValue) : "—"}</div>
                        </div>
                      );
                    }}
                  />
                  {/* Day separator lines */}
                  {intradayDayBoundaries.map((b) => (
                    <ReferenceLine
                      key={b.timestamp}
                      x={b.timestamp}
                      stroke="#94a3b8"
                      strokeDasharray="4 4"
                      strokeWidth={1.5}
                      label={{
                        value: b.dateStr,
                        position: "insideTopRight",
                        fontSize: 10,
                        fill: "#64748b",
                        fontWeight: 600,
                      }}
                    />
                  ))}
                  <Line
                    type="monotone"
                    dataKey="closeValue"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    dot={false}
                    name="Close"
                    connectNulls
                    isAnimationActive={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}
          {/* Adjustment Factors */}
          {selectedSymbol && !selectedSymbol.includes("/") && (
            <AdjustmentFactorsPanel symbol={selectedSymbol} />
          )}
        </div>
      </div>
    </div>
  );
}
